import { ArrowDownTrayIcon, CheckIcon } from '@heroicons/react/24/outline'
import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import {
  AiConversationExportFormat,
  exportAiConversation,
} from 'services/apis/aiProjects'

interface ChatExportContextValue {
  selecting: boolean
  selectedIds: Set<number>
  exporting: boolean
  canExport: boolean
  beginSelect: () => void
  cancelSelect: () => void
  toggleId: (id: number) => void
  selectAll: (ids: number[]) => void
  exportSelection: (format: AiConversationExportFormat) => Promise<void>
  exportMessage: (messageId: number, format: AiConversationExportFormat) => Promise<void>
}

const ChatExportContext = createContext<ChatExportContextValue | null>(null)

export const useChatExport = () => {
  const value = useContext(ChatExportContext)
  if (!value) throw new Error('useChatExport must be used within ChatExportProvider')
  return value
}

/** Optional hook for surfaces that may render outside the provider (e.g. welcome empty state). */
export const useChatExportOptional = () => useContext(ChatExportContext)

interface ChatExportProviderProps {
  children: ReactNode
  conversationId: number | null
  /** Loan stamped on the thread, or the loan currently open in the panel / workspace. */
  loanNumber?: number
  isRunning?: boolean
}

export function ChatExportProvider({
  children,
  conversationId,
  loanNumber,
  isRunning,
}: ChatExportProviderProps) {
  const [selecting, setSelecting] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set())
  const [exporting, setExporting] = useState(false)

  const canExport = !!conversationId && !!loanNumber && !isRunning && !exporting

  const beginSelect = useCallback(() => {
    setSelecting(true)
    setSelectedIds(new Set())
  }, [])

  const cancelSelect = useCallback(() => {
    setSelecting(false)
    setSelectedIds(new Set())
  }, [])

  const toggleId = useCallback((id: number) => {
    if (!id) return
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectAll = useCallback((ids: number[]) => {
    setSelectedIds(new Set(ids.filter((id) => id > 0)))
  }, [])

  const runExport = useCallback(
    async (ids: number[] | undefined, format: AiConversationExportFormat) => {
      if (!conversationId) {
        toast('Save the chat first, then export it.', { type: 'info' })
        return
      }
      if (!loanNumber) {
        toast('Open a loan to save this export into Documents.', { type: 'info' })
        return
      }
      setExporting(true)
      try {
        const result = await exportAiConversation(conversationId, {
          messageIds: ids,
          format,
          loanNumber,
        })
        toast(`Saved ${result.fileName} to loan ${result.loanNumber} Documents.`, { type: 'success' })
        setSelecting(false)
        setSelectedIds(new Set())
      } catch {
        // The global API handler surfaces the error.
      } finally {
        setExporting(false)
      }
    },
    [conversationId, loanNumber],
  )

  const exportSelection = useCallback(
    async (format: AiConversationExportFormat) => {
      const ids = Array.from(selectedIds)
      if (!ids.length) {
        toast('Select at least one message.', { type: 'info' })
        return
      }
      await runExport(ids, format)
    },
    [runExport, selectedIds],
  )

  const exportMessage = useCallback(
    async (messageId: number, format: AiConversationExportFormat) => {
      if (!messageId) {
        toast('That reply is not saved yet. Wait for the turn to finish, then try again.', { type: 'info' })
        return
      }
      await runExport([messageId], format)
    },
    [runExport],
  )

  const value = useMemo(
    () => ({
      selecting,
      selectedIds,
      exporting,
      canExport,
      beginSelect,
      cancelSelect,
      toggleId,
      selectAll,
      exportSelection,
      exportMessage,
    }),
    [
      selecting,
      selectedIds,
      exporting,
      canExport,
      beginSelect,
      cancelSelect,
      toggleId,
      selectAll,
      exportSelection,
      exportMessage,
    ],
  )

  return <ChatExportContext.Provider value={value}>{children}</ChatExportContext.Provider>
}

/** Format picker used by both the thread toolbar and a single-reply Save menu. */
export function ExportFormatMenu({
  disabled,
  onPick,
  label = 'Save',
  compact,
}: {
  disabled?: boolean
  onPick: (format: AiConversationExportFormat) => void
  label?: string
  compact?: boolean
}) {
  const [open, setOpen] = useState(false)

  return (
    <div className="relative">
      <button
        type="button"
        disabled={disabled}
        title="Save to loan Documents"
        aria-label={label}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className={
          compact
            ? 'flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-40'
            : 'flex items-center gap-1 rounded-md border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:border-shade-blue hover:text-shade-blue disabled:opacity-40'
        }
      >
        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
        {!compact && label}
      </button>
      {open && (
        <>
          <button type="button" className="fixed inset-0 z-10 cursor-default" aria-label="Close" onClick={() => setOpen(false)} />
          <div className="absolute right-0 z-20 mt-1 min-w-[9rem] rounded-md border border-gray-200 bg-white py-1 shadow-lg">
            {(['pdf', 'docx'] as AiConversationExportFormat[]).map((format) => (
              <button
                key={format}
                type="button"
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-shade-blue/5 hover:text-shade-blue"
                onClick={() => {
                  setOpen(false)
                  onPick(format)
                }}
              >
                {format === 'pdf' ? 'PDF document' : 'Word document'}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

/** Toolbar shown while multi-select is on, or the Export entry point when it is off. */
export function ChatExportToolbar({
  allMessageIds,
}: {
  allMessageIds: number[]
}) {
  const {
    selecting,
    selectedIds,
    canExport,
    exporting,
    beginSelect,
    cancelSelect,
    selectAll,
    exportSelection,
  } = useChatExport()

  if (!selecting) {
    return (
      <button
        type="button"
        disabled={!canExport || !allMessageIds.length}
        onClick={beginSelect}
        title={canExport ? 'Export messages to Documents' : 'Open a loan and wait for the chat to finish'}
        className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-600 hover:bg-white hover:text-shade-blue disabled:opacity-40"
      >
        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
        Export
      </button>
    )
  }

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <button
        type="button"
        className="rounded-md px-2 py-1 text-xs text-gray-600 hover:bg-white"
        onClick={() => selectAll(allMessageIds)}
      >
        Entire chat
      </button>
      <span className="text-[11px] text-gray-500">{selectedIds.size} selected</span>
      <ExportFormatMenu
        disabled={!canExport || selectedIds.size === 0}
        label={exporting ? 'Saving…' : 'Save'}
        onPick={(format) => exportSelection(format)}
      />
      <button type="button" className="rounded-md px-2 py-1 text-xs text-gray-500 hover:bg-white" onClick={cancelSelect}>
        Cancel
      </button>
    </div>
  )
}

export function MessageExportCheckbox({ messageId }: { messageId: number }) {
  const exportCtx = useChatExportOptional()
  if (!exportCtx?.selecting || !messageId) return null
  const checked = exportCtx.selectedIds.has(messageId)
  return (
    <button
      type="button"
      aria-pressed={checked}
      aria-label={checked ? 'Deselect message' : 'Select message'}
      onClick={() => exportCtx.toggleId(messageId)}
      className={`mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors ${
        checked ? 'border-shade-blue bg-shade-blue text-white' : 'border-gray-300 bg-white text-transparent'
      }`}
    >
      <CheckIcon className="h-3.5 w-3.5" />
    </button>
  )
}

/** Parse a runtime / DB message id into a positive number, or 0 when it is still a local placeholder. */
export function persistedMessageId(id: string | number | undefined | null): number {
  const n = Number(id)
  return Number.isFinite(n) && n > 0 ? n : 0
}
